// Moeda de ORIGEM de valores financeiros (item 15, Rafael 27/09: "o que for internacional
// precisa mostrar como deve ser em todos os lugares que temos valores").
//
// Espelho do backend `App\Services\Financeiro\MoedaOrigemFinanceiro` (mesmo formato):
//   { moeda, codigo, simbolo, cambio, cambio_medio, valor_origem, valor_brl, internacional,
//     misto, etapa, etapa_rotulo, fonte_cambio, por_moeda: [{moeda, simbolo, valor_origem, valor_brl, cambio}] }
// Resumos (listas/KPIs) vêm de `resumir()`: { internacional, valor_brl, por_moeda }.
//
// Regra de exibição: o valor principal SEMPRE em R$ (como hoje). Origem internacional ganha
// uma linha "US$ 1.000,00 · câmbio 5,10" logo abaixo. Nacional/legado (null) = nada muda.
// Moedas diferentes nunca são somadas: a quebra lista cada moeda com o seu R$.
import { moeda } from '../util/util';
import { simboloMoeda } from '../util/mascaraNumero';

const MOEDAS = ['real', 'dolar', 'euro', 'iene', 'libra', 'dolarcad'];

const numero = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : null;
};

export const normalizarMoedaOrigem = (m) => {
    const k = String(m || '').trim().toLowerCase();
    const apelidos = { brl: 'real', usd: 'dolar', eur: 'euro', jpy: 'iene', gbp: 'libra', cad: 'dolarcad', 'dólar': 'dolar' };
    const n = apelidos[k] || k;
    return MOEDAS.includes(n) ? n : 'real';
};

/** true quando há ao menos uma moeda estrangeira na origem. */
export const ehInternacional = (origem) => {
    if (!origem || typeof origem !== 'object') return false;
    if (origem.internacional === true) return true;
    return (origem.por_moeda || []).some((i) => normalizarMoedaOrigem(i.moeda) !== 'real');
};

/** "5,10" (até 4 casas; câmbio médio pode ter mais casas). */
export const formatarCambio = (c) => {
    const n = numero(c);
    if (n === null) return '';
    const casas = Math.abs(n * 100 - Math.round(n * 100)) < 1e-9 ? 2 : 4;
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: casas });
};

export const formatarValorMoeda = (valor, m) => {
    const simb = simboloMoeda(normalizarMoedaOrigem(m)) || 'R$';
    const n = numero(valor) || 0;
    return `${n < 0 ? '-' : ''}${simb} ${moeda(Math.abs(n))}`;
};

/** Itens estrangeiros da origem (BRL fica fora da linha de origem — já é o valor principal). */
export const itensEstrangeiros = (origem) => {
    if (!ehInternacional(origem)) return [];
    const lista = Array.isArray(origem.por_moeda) && origem.por_moeda.length
        ? origem.por_moeda
        : [{ moeda: origem.moeda, valor_origem: origem.valor_origem, valor_brl: origem.valor_brl, cambio: origem.cambio, cambio_medio: origem.cambio_medio }];
    return lista.filter((i) => normalizarMoedaOrigem(i.moeda) !== 'real');
};

/**
 * Texto da origem, curto: "US$ 1.000,00 · câmbio 5,10". Mistura: cada moeda com o seu R$
 * ("US$ 100,00 · câmbio 5,00 (R$ 500,00) + € 200,00 · câmbio 6,00 (R$ 1.200,00)").
 * @param comBrl força o "→ R$" (texto completo, ex.: title/tooltip)
 */
export const textoOrigem = (origem, comBrl = false) => {
    const itens = itensEstrangeiros(origem);
    if (!itens.length) return '';
    const varios = itens.length > 1 || (Array.isArray(origem.por_moeda) && origem.por_moeda.length > 1);
    const partes = itens.map((i) => {
        const c = formatarCambio(i.cambio);
        let t = formatarValorMoeda(i.valor_origem, i.moeda);
        if (c) t += ` · câmbio${i.cambio_medio ? ' médio' : ''} ${c}`;
        const brl = `R$ ${moeda(numero(i.valor_brl) || 0)}`;
        if (varios) t += ` (${brl})`;
        else if (comBrl) t += ` → ${brl}`;
        return t;
    });
    // Fornecedor/parcela MISTO (12/10): a parte em R$ também aparece ("US$ 100 ... + R$ 300").
    // Só na origem de um fornecedor/parcela — em somas (KPIs/grupos) o R$ dos nacionais não entra.
    if (origem.misto && Array.isArray(origem.por_moeda)) {
        const brlReal = origem.por_moeda.filter((i) => normalizarMoedaOrigem(i.moeda) === 'real')
            .reduce((s, i) => s + (numero(i.valor_brl) || 0), 0);
        if (Math.abs(brlReal) >= 0.005) partes.push(`R$ ${moeda(brlReal)}`);
    }
    return partes.join(' + ');
};

/**
 * Origem congelada de uma parcela a pagar (API: `origem_moeda`; ou colunas cruas
 * moeda_origem/cambio_origem). Legado sem origem → null.
 */
export const origemDaParcela = (parcela) => {
    if (!parcela) return null;
    if (parcela.origem_moeda && typeof parcela.origem_moeda === 'object') return parcela.origem_moeda;
    if (!parcela.moeda_origem) return null;
    const m = normalizarMoedaOrigem(parcela.moeda_origem);
    const brl = numero(parcela.valor) || 0;
    const cambio = m === 'real' ? 1 : numero(parcela.cambio_origem);
    // Previsão Inter: encargos/parte Brasil (brl_fora_cambio) não entram no valor na moeda.
    const fora = numero(parcela.brl_fora_cambio) || 0;
    const origem = cambio ? Math.round(((brl - fora) / cambio) * 100) / 100 : numero(parcela.valor_origem);
    return { moeda: m, simbolo: simboloMoeda(m), cambio, valor_origem: origem, valor_brl: brl, internacional: m !== 'real',
        por_moeda: [{ moeda: m, valor_origem: origem, valor_brl: brl, cambio }] };
};

/**
 * Soma origens (KPIs/totais de grupo) SEM misturar moedas: por moeda, valor na moeda e R$.
 * Aceita origens de fornecedor, de parcela ou resumos. Retorna null se nada for internacional.
 */
export const somarOrigens = (origens) => {
    const acc = {};
    let total = 0;
    let algum = false;
    (origens || []).forEach((o) => {
        if (!ehInternacional(o)) return;
        algum = true;
        (o.por_moeda && o.por_moeda.length ? o.por_moeda : [o]).forEach((i) => {
            const m = normalizarMoedaOrigem(i.moeda);
            if (!acc[m]) acc[m] = { moeda: m, valor_origem: 0, valor_brl: 0, cambios: new Set(), medio: false };
            acc[m].valor_origem += numero(i.valor_origem) || 0;
            acc[m].valor_brl += numero(i.valor_brl) || 0;
            if (numero(i.cambio) !== null) acc[m].cambios.add(Math.round(numero(i.cambio) * 1e6) / 1e6);
            if (i.cambio_medio) acc[m].medio = true;
            total += numero(i.valor_brl) || 0;
        });
    });
    if (!algum) return null;
    const por_moeda = Object.values(acc).map((d) => {
        const origem = Math.round(d.valor_origem * 100) / 100;
        const brl = Math.round(d.valor_brl * 100) / 100;
        const cambios = Array.from(d.cambios);
        const cambio = d.moeda === 'real' ? 1 : (cambios.length === 1 ? cambios[0] : (origem ? Math.round((brl / origem) * 1e6) / 1e6 : null));
        return { moeda: d.moeda, simbolo: simboloMoeda(d.moeda), valor_origem: origem, valor_brl: brl, cambio,
            cambio_medio: d.moeda !== 'real' && (cambios.length > 1 || d.medio) };
    });
    return { internacional: true, valor_brl: Math.round(total * 100) / 100, por_moeda };
};

/**
 * Mesma origem aplicada a OUTRO valor em R$ do mesmo fornecedor (ex.: soma das parcelas do plano,
 * que é o que a aprovação congela): valor na moeda = R$ ÷ câmbio. Mistura: proporcional por moeda.
 */
export const origemParaValor = (origem, valorBrl) => {
    if (!ehInternacional(origem)) return null;
    const alvo = numero(valorBrl) || 0;
    const itens = Array.isArray(origem.por_moeda) && origem.por_moeda.length ? origem.por_moeda
        : [{ moeda: origem.moeda, valor_origem: origem.valor_origem, valor_brl: origem.valor_brl, cambio: origem.cambio, cambio_medio: origem.cambio_medio }];
    const base = itens.reduce((s, i) => s + (numero(i.valor_brl) || 0), 0);
    const por_moeda = itens.map((i) => {
        const m = normalizarMoedaOrigem(i.moeda);
        const brl = itens.length === 1 ? alvo : (base ? Math.round(alvo * ((numero(i.valor_brl) || 0) / base) * 100) / 100 : 0);
        const c = m === 'real' ? 1 : numero(i.cambio);
        const orig = c ? Math.round((brl / c) * 100) / 100 : 0;
        return { ...i, moeda: m, valor_brl: brl, valor_origem: orig };
    });
    const unico = por_moeda.length === 1 ? por_moeda[0] : null;
    return { ...origem, valor_brl: alvo, valor_origem: unico ? unico.valor_origem : null, por_moeda };
};

/**
 * Origem de um PLANO (OPF) para exibir junto de um valor em R$.
 * - Aprovado: a API devolve a origem CONGELADA nas parcelas a pagar (`congelada: true`) → usa
 *   como está (câmbio da aprovação), nunca re-divide pelo câmbio atual. Aprovado sem origem
 *   congelada (legado) vem null → só R$.
 * - Pendente (ainda não aprovado): origem derivada, aplicada ao valor exibido.
 */
export const origemDoPlano = (origem, valorBrl) => {
    if (!origem) return null;
    if (origem.congelada) return origem;
    return origemParaValor(origem, valorBrl);
};
